// MessageOptionsMenu.js - WhatsApp-style horizontal options on hover
import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Reply as ReplyIcon,
  AddReaction as AddReactionIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import ReactionPicker from './ReactionPicker';
import { toast } from 'sonner';

export default function MessageOptionsMenu({
  message,
  isUserMessage,
  isTcc,
  onReply,
  onQuote,
  onReact,
  onReactionAdd,
  onReactionRemove,
  onDelete,
  deleteLoading = false
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const [reactionPickerAnchor, setReactionPickerAnchor] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReply = () => {
    handleClose();
    if (onReply) {
      onReply(message);
    }
  };

  const handleReactionMenuClick = (event) => {
    setReactionPickerAnchor(event.currentTarget);
    setReactionPickerOpen(true);
  };

  const handleReactionSelect = async (reactionType) => {
    const activeReactions = Array.isArray(message?.user_reactions) ? message.user_reactions : [];
    try {
      if (activeReactions.includes(reactionType)) {
        if (onReactionRemove) {
          await onReactionRemove(message.id, reactionType);
          toast.info('Reaction removed');
        }
      } else {
        if (onReactionAdd) {
          await onReactionAdd(message.id, reactionType);
          toast.success('Reaction added', {
            description: `Added ${reactionType} reaction`
          });
        }
      }
    } catch (error) {
      toast.error('Failed to update reaction', {
        description: error.message
      });
    }
    
    setReactionPickerOpen(false);
  };

  const handleReactionPickerClose = () => {
    setReactionPickerOpen(false);
  };

  const handleDelete = () => {
    handleClose();
    if (onDelete) {
      onDelete(message?.id);
    }
  };

  return (
    <>
      {/* WhatsApp-style horizontal options on hover */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
        }}
      >
        {/* Reply Option */}
        <Tooltip title="Reply" placement="top">
          <IconButton
            size="small"
            onClick={handleReply}
            sx={{
              width: 28,
              height: 28,
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            <ReplyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        {/* React Option */}
        <Tooltip title="React" placement="top">
          <IconButton
            size="small"
            onClick={handleReactionMenuClick}
            sx={{
              width: 28,
              height: 28,
              color: 'text.secondary',
              '&:hover': {
                color: 'secondary.main',
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
              },
            }}
          >
            <AddReactionIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        {/* Delete Option (only for TCC) */}
        {isTcc && (
          <Tooltip title="Delete" placement="top">
            <IconButton
              size="small"
              onClick={handleDelete}
              disabled={deleteLoading}
              sx={{
                width: 28,
                height: 28,
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main',
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                },
              }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <ReactionPicker
        open={reactionPickerOpen}
        anchorEl={reactionPickerAnchor}
        onClose={handleReactionPickerClose}
        onReactionSelect={handleReactionSelect}
        currentReactions={Array.isArray(message?.user_reactions) ? message.user_reactions : []}
      />
    </>
  );
}
